import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { fetchRevealPlannedTeam } from '@api-service/content';
import type { RevealTeamPerspective } from '@api-service/content';
import type { PlannerGameweek } from '@api-service/planner';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { hasSufficientPrivileges, ROLE_LEVELS } from '@lib/utils';

// The plan only changes when the contributor edits it, so a member moving
// between reveals need not refetch one they have just seen.
const PLANNED_TEAM_STALE_TIME_IN_MS = 60 * 1000;

export type RevealPlannedTeamState =
  | { status: 'loading' }
  | { status: 'ready'; gameweek: PlannerGameweek }
  | { status: 'unavailable' };

const readRouteParameter = (
  value: string | string[] | undefined
): string | undefined => (typeof value === 'string' ? value : undefined);

/**
 * The planned team for one team box, read by a member's browser with their
 * token; the API is the real gate. Anyone without Pro or Ultra, and any
 * failure, is `unavailable`.
 *
 * The reveal comes from the route (/team-reveals/<author>/<slug>, preview
 * included) because body blocks are rendered without the page document. On
 * the preview route the box is read from the draft, so an editor sees the
 * FPL ID they have just entered.
 */
export const useRevealPlannedTeam = (
  blockKey: string
): RevealPlannedTeamState => {
  const { query } = useRouter();
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  const authorSlug = readRouteParameter(query.author);
  const slug = readRouteParameter(query.slug);
  const perspective: RevealTeamPerspective =
    query.preview === undefined ? 'published' : 'drafts';
  const isEntitled = hasSufficientPrivileges(user, ROLE_LEVELS.PRO);
  const hasReveal = Boolean(authorSlug && slug && blockKey);

  const { data, isSuccess, isError } = useQuery<PlannerGameweek | null>({
    queryKey: [
      'reveal-planned-team',
      authorSlug,
      slug,
      blockKey,
      perspective,
      user?.sub
    ],
    queryFn: async () => {
      if (!authorSlug || !slug || !accessToken) return null;
      const gameweek = await fetchRevealPlannedTeam(
        { type: 'teamReveal', authorSlug, slug, blockKey, perspective },
        accessToken
      );
      return gameweek ?? null;
    },
    enabled: isEntitled && hasReveal && Boolean(accessToken),
    staleTime: PLANNED_TEAM_STALE_TIME_IN_MS,
    refetchOnWindowFocus: false,
    retry: false
  });

  if (isUserLoading) return { status: 'loading' };
  if (!isEntitled || !hasReveal) return { status: 'unavailable' };
  if (!accessToken) {
    return accessTokenIsFetching
      ? { status: 'loading' }
      : { status: 'unavailable' };
  }
  if (isError) return { status: 'unavailable' };
  if (isSuccess) {
    return data
      ? { status: 'ready', gameweek: data }
      : { status: 'unavailable' };
  }
  return { status: 'loading' };
};
